import React from 'react'
import maplibregl from 'maplibre-gl'
import dayjs from 'dayjs'
import { useGlobalState } from 'state'
import * as api from 'common/api'
import { TimeSeriesRow } from 'common/types'
import { RainTileLayer } from 'components/layers/RainLayer/RainTileLayer'
import { RainWideLayer } from 'components/layers/RainLayer/RainWideLayer'
import { TimeSeriesSlider } from 'components/layers/TimeSeriesSlider'

export const RainLayer = () => {
  const state = useGlobalState()
  const [currentTileBounds, setCurrentTileBounds] = React.useState<maplibregl.LngLatBounds | null>(null)
  const [rainTiles, setRainTiles] = React.useState<{code: string; bounds: maplibregl.LngLatBounds}[]>([])
  const [sliderIndex, setSliderIndex] = React.useState(-1)
  const [sliderBaseDate, setSliderBaseDate] = React.useState<dayjs.Dayjs>(dayjs.utc())
  const [timeSeries, setTimeSeries] = React.useState<TimeSeriesRow[]>([])
  const [opacity, setOpacity] = React.useState<number>(state.map.layerOpacity['rain'])

  const isTileLayerZoom: boolean = React.useMemo(() => state.map.intZoom >= 9, [state.map.intZoom])

  const currentRainRow = React.useMemo((): TimeSeriesRow | null => {
    return timeSeries.length === 0 || timeSeries.length <= sliderIndex || sliderIndex < 0 ? null : timeSeries[sliderIndex]
  }, [timeSeries, sliderIndex])

  const tileRainRow = React.useMemo((): TimeSeriesRow | null => {
    return currentRainRow && isTileLayerZoom && currentRainRow.tileUrl ? currentRainRow : null
  }, [currentRainRow, isTileLayerZoom])

  const wideRainRow = React.useMemo((): TimeSeriesRow | null => {
    return currentRainRow && isTileLayerZoom && currentRainRow.tileUrl ? null : currentRainRow
  }, [currentRainRow, isTileLayerZoom])

  React.useEffect(() => {
    if (state.mapbox) {
      fetchRain()
    }
  }, [state.mapbox, state.app.baseDate])

  // opacity
  React.useEffect(() => {
    setOpacity(state.map.layerOpacity['rain'])
  }, [state.map.layerOpacity['rain']])

  React.useEffect(() => {
    if (!state.mapbox) return
    if (!isTileLayerZoom) return
    if (currentTileBounds !== null) {
      if (
        currentTileBounds.contains(state.map.bounds.getNorthEast()) &&
        currentTileBounds.contains(state.map.bounds.getSouthWest())
      ) {
        return
      }
    }
    fetchRain()
  }, [state.map.bounds])

  const fetchRain = async () => {
    if (!state.mapbox) return
    const northwest = state.mapbox.getBounds().getNorthWest()
    const southeast = state.mapbox.getBounds().getSouthEast()
    const params = {
      northwest_lat: northwest.lat,
      northwest_lng: northwest.lng,
      southeast_lat: southeast.lat,
      southeast_lng: southeast.lng,
      date: state.app.getFormattedBaseDate(5)
    }
    const res = await api.get('/rain', params)
    const tiles: {
      code: string
      bounds: number[][]
    }[] = res.tiles

    if (tiles.length === 0 || !isTileLayerZoom) {
      setCurrentTileBounds(null)
      setRainTiles([])
    } else {
      // ロードしているタイル境界を保持
      const north = tiles.map(t => t.bounds[0][0])
      const west = tiles.map(t => t.bounds[0][1])
      const south = tiles.map(t => t.bounds[1][0])
      const east = tiles.map(t => t.bounds[1][1])
      north.push(northwest.lat)
      west.push(northwest.lng)
      south.push(southeast.lat)
      east.push(southeast.lng)
      const sw = new maplibregl.LngLat(
        Math.min(...west),
        Math.min(...south)
      )
      const ne = new maplibregl.LngLat(
        Math.max(...east),
        Math.max(...north)
      )
      const bounds = new maplibregl.LngLatBounds(sw, ne)
      setCurrentTileBounds(bounds)

      // 表示するタイルデータ
      const convertTiles = tiles.map(tile => {
        const sw = new maplibregl.LngLat(tile.bounds[0][1], tile.bounds[1][0])
        const ne = new maplibregl.LngLat(tile.bounds[1][1], tile.bounds[0][0])
        return {
          code: tile.code,
          bounds: new maplibregl.LngLatBounds(sw, ne)
        }
      })
      setRainTiles(convertTiles)
    }

    // スライダー 時系列
    const rains: TimeSeriesRow[] = res.rains
    if (timeSeries.length === 0 || !sliderBaseDate.isSame(dayjs.utc(res.baseDate))) {
      setTimeSeries(rains)
      const baseDateIndex = rains.findIndex(rain => rain.date === res.baseDate)
      setSliderIndex(baseDateIndex)
      setSliderBaseDate(dayjs.utc(res.baseDate))
    }
  }

  const sliderMoveend = (index: number) => {
    setSliderIndex(index)
  }

  return (
    <>
      { tileRainRow && rainTiles.map(tile => <RainTileLayer key={tile.code} code={tile.code} bounds={tile.bounds} rain={tileRainRow} opacity={opacity} />) }
      <RainWideLayer rain={wideRainRow} opacity={opacity}  />
      <TimeSeriesSlider baseDate={sliderBaseDate} currentIndex={sliderIndex} timeSeries={timeSeries} moveend={sliderMoveend} />
    </>
  )
}
